/**
 * Custom Hooks
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Auto-save hook - Saves to localStorage immediately (with debounce)
 * Saves on EVERY change after a short delay to prevent data loss
 * @param key - localStorage key
 * @param data - data to save
 * @param delay - debounce delay in ms (default: 500ms)
 */
export const useAutoSave = (key, data, delay = 500) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (!data || !key) return;

    // Debounce to avoid excessive writes, but save quickly (500ms default)
    const timer = setTimeout(() => {
      setIsSaving(true);
      try {
        // Save to localStorage immediately - instant, offline-safe
        localStorage.setItem(key, JSON.stringify({
          data,
          timestamp: new Date().toISOString(),
          version: 1
        }));
        setLastSaved(new Date());
      } catch (error) {
        console.error('Auto-save to localStorage failed:', error);
        if (error.name === 'QuotaExceededError') {
          console.error('localStorage is full. Consider clearing old data.');
        }
      } finally {
        setIsSaving(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [key, data, delay]);

  return { isSaving, lastSaved };
};

/**
 * Load saved data from localStorage
 */
export const useLoadSavedData = (key) => {
  const [savedData, setSavedData] = useState(null);
  const [lastSavedTime, setLastSavedTime] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSavedData(parsed.data);
        setLastSavedTime(parsed.timestamp);
      }
    } catch (error) {
      console.error('Failed to load saved data:', error);
    }
  }, [key]);

  const clearSavedData = () => {
    localStorage.removeItem(key);
    setSavedData(null);
    setLastSavedTime(null);
  };

  return { savedData, lastSavedTime, clearSavedData };
};

/**
 * Background sync hook - Syncs localStorage to database periodically
 * Less frequent than auto-save (default: 5 minutes)
 */
export const useBackgroundSync = (key, syncFunction, delay = 300000) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const saved = localStorage.getItem(key);
        if (!saved) return;

        const parsed = JSON.parse(saved);
        setIsSyncing(true);
        setSyncError(null);

        // Sync to database
        await syncFunction(parsed.data);
        
        setLastSynced(new Date());
      } catch (error) {
        console.error('Background sync failed:', error);
        setSyncError(error.message);
        // Keep data in localStorage if sync fails
      } finally {
        setIsSyncing(false);
      }
    }, delay);

    return () => clearInterval(timer);
  }, [key, syncFunction, delay]);

  // Sync on page unload
  useEffect(() => {
    const handleBeforeUnload = async () => {
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          await syncFunction(parsed.data);
        }
      } catch (error) {
        console.error('Sync on unload failed:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [key, syncFunction]);

  return { isSyncing, lastSynced, syncError };
};

/**
 * Local storage hook
 * Syncs state with localStorage
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};

/**
 * Debounce hook
 * Delays execution until value stops changing
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Form validation hook
 */
export const useFormValidation = (initialValues, validationRules) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validate = useCallback((fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return '';

    for (const rule of rules) {
      const error = rule(value, values);
      if (error) return error;
    }
    return '';
  }, [validationRules, values]);

  const handleChange = useCallback((fieldName, value) => {
    setValues(prev => ({ ...prev, [fieldName]: value }));
    
    if (touched[fieldName]) {
      const error = validate(fieldName, value);
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  }, [touched, validate]);

  const handleBlur = useCallback((fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const error = validate(fieldName, values[fieldName]);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  }, [validate, values]);

  const validateAll = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const error = validate(fieldName, values[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched(Object.keys(validationRules).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}));

    return isValid;
  }, [validate, validationRules, values]);

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    setValues,
  };
};
